import { usePublicBootstrap } from '../seo/PublicBootstrap.jsx';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const RECENT_SEARCH_STORAGE_KEY = 'mts.directory.recentSearches';
const MAX_RECENT_SEARCHES = 5;

function getPrimaryCategoryName(listing) {
  if (typeof listing?.category === 'string' && listing.category.trim()) {
    return listing.category.trim();
  }

  const categoryFromRelations = listing?.listingCategories?.[0]?.tenantCategory?.name;
  if (typeof categoryFromRelations === 'string' && categoryFromRelations.trim()) {
    return categoryFromRelations.trim();
  }

  return 'Local Business';
}

export function mapListingToCardListing(listing) {
  const cityState = [listing?.city, listing?.state]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(', ');
  const address = listing?.address || listing?.addressLine1 || cityState;

  return {
    ...listing,
    category: getPrimaryCategoryName(listing),
    rating: typeof listing?.rating === 'number' ? listing.rating : Number(listing?.rating || 0),
    reviewCount: typeof listing?.reviewCount === 'number'
      ? listing.reviewCount
      : Number(listing?.reviewCount || listing?.reviews || 0),
    address,
    shortAddress: listing?.shortAddress || listing?.addressLine1 || address,
    description: listing?.description || listing?.shortDescription || 'No description available',
  };
}

export function useDirectorySearchState() {
  const bootstrap = usePublicBootstrap();
  const [params, setParams] = useSearchParams();
  const committedQuery = params.get('q') || '';
  // Associate a draft with its source URL so navigation invalidates pending edits.
  const urlKey = params.toString();
  const [draft, setDraft] = useState(null);
  const searchQuery = draft?.urlKey === urlKey ? draft.value : committedQuery;
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RECENT_SEARCH_STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter(item => typeof item === 'string' && item.trim()).slice(0, MAX_RECENT_SEARCHES) : [];
    } catch { return []; }
  });
  const [isDesktopCardLayout, setIsDesktopCardLayout] = useState(() =>
    !bootstrap && typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches);

  const update = (values, options = {}) => {
    setParams(current => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(values)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      next.delete('page');
      return next;
    }, options);
  };

  useEffect(() => {
    setDraft(current => current?.urlKey === urlKey ? current : null);
  }, [urlKey]);

  useEffect(() => {
    if (draft?.urlKey !== urlKey || searchQuery.trim() === committedQuery) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(urlKey);
      if (searchQuery.trim()) next.set('q', searchQuery.trim());
      else next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
      setDraft(null);
    }, 200);
    return () => clearTimeout(timer);
  }, [draft, urlKey, searchQuery, committedQuery, setParams]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1280px)');
    const change = () => setIsDesktopCardLayout(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);

  const applySearchTerm = term => {
    const value = String(term || '').trim();
    setDraft(null);
    update({ q: value });
    if (!value) return;
    setRecentSearches(current => {
      const next = [value, ...current.filter(item => item.toLowerCase() !== value.toLowerCase())].slice(0, MAX_RECENT_SEARCHES);
      try { window.localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(next)); } catch { /* Storage is optional. */ }
      return next;
    });
  };

  return {
    searchQuery,
    committedQuery,
    setSearchQuery: value => setDraft({ urlKey, value }),
    applySearchTerm,
    categoryFilter: params.get('category')?.trim() || 'all',
    setCategoryFilter: value => { setDraft(null); update({ category: value === 'all' ? null : value, q: searchQuery.trim() }); },
    sortBy: params.get('sort') || 'name',
    setSortBy: value => update({ sort: value === 'name' ? null : value }),
    showOpenOnly: params.get('openOnly') === 'true',
    setShowOpenOnly: value => update({ openOnly: value ? 'true' : null }),
    isDesktopCardLayout,
    recentSearches,
    mapListingToCardListing,
    resetFilters: () => {
      setDraft(null);
      update({ q: null, category: null, sort: null, openOnly: null, tagIds: null, matchMode: null, city: null });
    },
  };
}

export default useDirectorySearchState;
